import { useCallback, useEffect, useMemo, useState } from 'react';

export function useEnhancedMeasuresAccordions (enhancedMeasures) {
    const { enhancedMeasuresPerAssetOwner, actorIds } = useMemo(() => {
        const actorIds = [];
        const measuresPerAssetOwner = enhancedMeasures.reduce((acc, enhancedMeasure) => {
            const nuarActorId = enhancedMeasure.sensitiveAssetData.dataproviderid_fk;
            if (!nuarActorId) {
                return acc;
            }

            if (!acc[nuarActorId]) {
                actorIds.push(nuarActorId);
                acc[nuarActorId] = [];
            }

            acc[nuarActorId].push(enhancedMeasure);
            return acc;
        }, {});

        return {
            enhancedMeasuresPerAssetOwner: measuresPerAssetOwner,
            actorIds
        };
    }, [enhancedMeasures]);

    const [openAccordions, setOpenAccordions] = useState([]);

    useEffect(() => {
        setOpenAccordions((prev) => {
            return actorIds
                .filter(Boolean)
                .map((id) => {
                    const existing = prev.find((a) => a.id === id);
                    return existing ?? { id, isOpen: true };
                });
        });
    }, [actorIds]);

    const allAccordionsClosed = openAccordions.every((a) => !a.isOpen);

    const setAllAccordionsOpen = useCallback((isOpen) => {
        setOpenAccordions((prev) => prev.map((a) => ({ ...a, isOpen })));
    }, []);

    const toggleAllAccordions = useCallback(() => {
        setOpenAccordions((prev) => {
            const shouldOpen = prev.every((a) => !a.isOpen);
            return prev.map((a) => ({ ...a, isOpen: shouldOpen }));
        });
    }, []);

    const toggleAccordion = useCallback((actorId, isOpen) => {
        setOpenAccordions((prev) => {
            const index = prev.findIndex((a) => a.id === actorId);
            if (index === -1) {
                return [...prev, { id: actorId, isOpen }];
            }
            return prev.map((a, i) => (i === index ? { ...a, isOpen } : a));
        });
    }, []);

    const isAccordionOpen = useCallback(
        (actorId) => openAccordions.find((a) => a.id === actorId)?.isOpen ?? false,
        [openAccordions]
    );
    return {
        actorIds,
        enhancedMeasuresPerAssetOwner,
        allAccordionsClosed,
        setAllAccordionsOpen,
        toggleAllAccordions,
        toggleAccordion,
        isAccordionOpen
    };
}
